import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import dayjs from "dayjs";
import commoncss from "../css/common.module.css";
import { ReactComponent as CalenderIcon } from "../assets/icons/calender.svg";

const parseDDMMYYYY = (str) => {
  if (!str) return null;
  const parts = str.split(/\D+/).map((p) => p.trim());
  if (parts.length < 3) return null;
  const dd = parseInt(parts[0], 10);
  const mm = parseInt(parts[1], 10);
  const yyyy = parseInt(parts[2], 10);
  if (isNaN(dd) || isNaN(mm) || isNaN(yyyy)) return null;
  const date = new Date(yyyy, mm - 1, dd);
  if (date && date.getDate() === dd && date.getMonth() === mm - 1 && date.getFullYear() === yyyy) return date;
  return null;
};

const CommonDatePicker = ({ value, onChange, placeholder = "Select Date", containerClass, containerStyle, inputClass, inputStyle, iconStyle, placement = "bottom", disabled = false }) => {
  const isDisabled = Boolean(disabled);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value ? dayjs(value).format("DD-MM-YYYY") : "");
  const wrapperRef = useRef(null);
  const calendarRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, placement: "bottom" });

  useEffect(() => {
    if (value) {
      setText(dayjs(value).format("DD-MM-YYYY"));
    } else {
      setText("");
    }
  }, [value]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        wrapperRef.current && 
        !wrapperRef.current.contains(event.target) &&
        calendarRef.current &&
        !calendarRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOpen = () => {
    if (isDisabled) return;
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setCoords({
        top: placement === 'top' ? rect.top + window.scrollY : rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        placement: placement,
      });
    }
    setOpen(true);
  };

  const handleBlur = (e) => {
    if (isDisabled) return;
    if (!e.target.value || e.target.value.trim() === "") {
      onChange(null);
      return;
    }
    const parsed = parseDDMMYYYY(e.target.value);
    if (parsed) {
      onChange(parsed);
    } else {
      if (value) setText(dayjs(value).format("DD-MM-YYYY"));
      else setText("");
    }
  };

  const handleChangeText = (e) => {
    if (isDisabled) return;
    setText(e.target.value);
    if (!e.target.value || e.target.value.trim() === "") {
       onChange(null);
    } else {
       const parsed = parseDDMMYYYY(e.target.value);
       if (parsed) onChange(parsed);
    }
  }

  const handleCalendarChange = (newDate) => {
    if (isDisabled) return;
    onChange(newDate);
    setOpen(false);
  };

  const customStyles = `
    .react-calendar-custom {
      width: 230px !important;
      border: none !important;
      font-family: 'DM Sans', sans-serif !important;
    }
    .react-calendar-custom .react-calendar__navigation {
      height: 40px !important;
      padding-bottom: 10px !important;
      margin-bottom: 12px !important;
      border-bottom: 1px solid #eaeeed !important;
      margin-left: -10px !important;
      margin-right: -10px !important;
      padding-left: 10px !important;
      padding-right: 10px !important;
    }
    .react-calendar-custom .react-calendar__navigation button {
      min-width: 25px !important;
      background: none;
    }
    .react-calendar-custom .react-calendar__navigation__arrow {
      font-size: 20px !important;
      padding: 0 !important;
      color: #888 !important;
      transition: color 0.2s;
    }
    .react-calendar-custom .react-calendar__navigation__arrow:hover {
      color: #2d8861 !important;
      background: transparent !important;
    }
    .react-calendar-custom .react-calendar__navigation__label {
      font-size: 14px !important;
      font-weight: 600 !important;
      color: #205a46 !important;
    }
    .react-calendar-custom .react-calendar__navigation__label:hover,
    .react-calendar-custom .react-calendar__navigation__label:active,
    .react-calendar-custom .react-calendar__navigation__label:focus {
      background: #f4faf5 !important;
      border-radius: 6px !important;
    }
    .react-calendar-custom .react-calendar__month-view__weekdays {
      font-size: 11px !important;
      font-weight: 500 !important;
      color: #888 !important;
    }
    .react-calendar-custom .react-calendar__month-view__weekdays__weekday {
      padding: 4px 2px !important;
      margin-bottom: 8px !important;
    }
    .react-calendar-custom .react-calendar__month-view__weekdays__weekday abbr {
      text-decoration: none !important;
    }
    .react-calendar-custom .react-calendar__tile {
      padding: 6px 2px !important; 
      font-size: 12px !important;
      border-radius: 6px !important;
      color: #777;
      margin-bottom: 6px !important;
    }
    .react-calendar-custom .react-calendar__year-view .react-calendar__tile,
    .react-calendar-custom .react-calendar__decade-view .react-calendar__tile,
    .react-calendar-custom .react-calendar__century-view .react-calendar__tile {
      padding: 10px 2px !important;
    }
    .react-calendar-custom .react-calendar__tile:hover {
      background: #f4faf5 !important;
      border-radius: 6px !important;
      color: #2d8861 !important;
    }
    .react-calendar-custom .react-calendar__tile--active,
    .react-calendar-custom .react-calendar__tile--hasActive,
    .react-calendar-custom .react-calendar__tile--active.react-calendar__month-view__days__day--weekend,
    .react-calendar-custom .react-calendar__tile--active.react-calendar__month-view__days__day--neighboringMonth,
    .react-calendar-custom .react-calendar__tile--hasActive.react-calendar__month-view__days__day--weekend,
    .react-calendar-custom .react-calendar__tile--hasActive.react-calendar__month-view__days__day--neighboringMonth {
      background: #2d8861 !important;
      color: #fff !important; 
      border-radius: 6px !important;
      font-weight: 600;
    }
    .react-calendar-custom .react-calendar__year-view .react-calendar__tile--active,
    .react-calendar-custom .react-calendar__year-view .react-calendar__tile--hasActive,
    .react-calendar-custom .react-calendar__decade-view .react-calendar__tile--active,
    .react-calendar-custom .react-calendar__decade-view .react-calendar__tile--hasActive,
    .react-calendar-custom .react-calendar__century-view .react-calendar__tile--active,
    .react-calendar-custom .react-calendar__century-view .react-calendar__tile--hasActive {
      background: #2d8861 !important;
      color: #fff !important; 
      border-radius: 6px !important;
    }
    .react-calendar-custom .react-calendar__tile--active:enabled:hover,
    .react-calendar-custom .react-calendar__tile--active:enabled:focus,
    .react-calendar-custom .react-calendar__tile--hasActive:enabled:hover,
    .react-calendar-custom .react-calendar__tile--hasActive:enabled:focus {
      background: #205a46 !important;
      color: #fff !important;
    }
    .react-calendar-custom .react-calendar__tile--now {
      background: transparent !important;
    }
    .react-calendar-custom .react-calendar__tile--now:hover {
      background: #f4faf5 !important;
    }
    .react-calendar-custom .react-calendar__month-view__days__day--weekend {
      color: #555 !important;
    }
    .react-calendar-custom .react-calendar__month-view__days__day--neighboringMonth {
      color: #cccccc !important;
    }
    .react-calendar-custom .react-calendar__month-view__days,
    .react-calendar-custom .react-calendar__month-view__weekdays {
      display: grid !important;
      grid-template-columns: repeat(7, 1fr) !important;
      gap: 4px !important;
    }
    .react-calendar-custom .react-calendar__year-view__months,
    .react-calendar-custom .react-calendar__decade-view__years,
    .react-calendar-custom .react-calendar__century-view__decades {
      display: grid !important;
      grid-template-columns: repeat(3, 1fr) !important;
      gap: 12px 8px !important;
    }
    .react-calendar-custom .react-calendar__tile,
    .react-calendar-custom .react-calendar__month-view__weekdays__weekday {
      max-width: 100% !important;
      width: 100% !important;
    }
  `;

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
      <style>{customStyles}</style>
      <div 
        className={containerClass !== undefined ? containerClass : commoncss.adjBox} 
        onClick={handleOpen} 
        style={{
          ...containerStyle,
          ...(isDisabled ? { cursor: 'not-allowed', backgroundColor: '#f5f5f5', opacity: 0.7 } : {})
        }}
      >
        <input
          type="text"
          className={inputClass}
          value={text}
          placeholder={placeholder}
          disabled={isDisabled}
          onFocus={handleOpen}
          onChange={handleChangeText}
          onBlur={handleBlur}
          style={{ 
            outline: 'none', 
            width: '100%', 
            border: containerClass !== undefined ? undefined : 'none', 
            background: containerClass !== undefined ? undefined : 'transparent',
            ...(isDisabled ? { cursor: 'not-allowed', color: '#888' } : {}),
            ...inputStyle 
          }}
        />
        <CalenderIcon 
          width={20} 
          height={20} 
          onClick={(e) => {
            e.stopPropagation();
            if (isDisabled) return;
            if (open) setOpen(false); else handleOpen();
          }} 
          style={{ 
            cursor: isDisabled ? "not-allowed" : "pointer", 
            opacity: isDisabled ? 0.5 : 1,
            ...iconStyle 
          }} 
        />
      </div>

      {!isDisabled && open && ReactDOM.createPortal(
        <div ref={calendarRef} style={{
          position: 'absolute',
          top: coords.placement === 'top' ? coords.top - 5 : coords.top + 5,
          left: coords.left,
          transform: coords.placement === 'top' ? 'translateY(-100%)' : 'none',
          zIndex: 999999,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          borderRadius: '8px',
          background: 'white',
          padding: '10px'
        }}>
          <Calendar
            onChange={handleCalendarChange}
            value={value ? new Date(value) : null}
            className="react-calendar-custom"
            prevLabel={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>}
            nextLabel={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>}
            prev2Label={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="11 17 6 12 11 7"></polyline><polyline points="18 17 13 12 18 7"></polyline></svg>}
            next2Label={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="13 17 18 12 13 7"></polyline><polyline points="6 17 11 12 6 7"></polyline></svg>}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

export default CommonDatePicker;
